import { round } from "lodash-es";

import { Org } from "./Types";
import { collectUnitSymbols } from "./columns";
import { isDemoUsa } from "./orgRole";

// Which system an org reads its measurements in. For now only the Demo USA
// portal reads imperial (lb, in², in, °F); every real org stays metric until
// US customers are switched over deliberately (then key this off the org's
// country, Country.US, as the date formatter already does).

export type UnitSystem = "metric" | "imperial";
export type WeightUnit = "kg" | "lb";
export type TemperatureUnit = "°C" | "°F";

/** Rendered unit symbols for the measurements the pages show. */
export type DisplayUnits = {
  weight: WeightUnit;
  area: string;
  depth: string;
  temperature: TemperatureUnit;
};

/** The carcase measurements the Data Analysis Overview pages render. */
export type CarcaseUnits = Pick<DisplayUnits, "weight" | "area" | "depth">;

export const UNIT_SYMBOLS: Record<UnitSystem, DisplayUnits> = {
  metric: { weight: "kg", area: "cm²", depth: "mm", temperature: "°C" },
  imperial: { weight: "lb", area: "in²", depth: "in", temperature: "°F" },
};

export const unitSystemFor = (org: Org): UnitSystem =>
  isDemoUsa(org) ? "imperial" : "metric";

/**
 * A temperature the device recorded in Celsius, in the display unit. Only
 * the rendering converts: thresholds and range checks stay in the stored °C.
 */
export const toDisplayTemperature = (
  celsius: number,
  unit: TemperatureUnit,
): number => (unit === "°F" ? (celsius * 9) / 5 + 32 : celsius);

/**
 * A recorded °C reading rounded for display in `unit`, or null when there is
 * no reading. Null must stay null: 0 °C would read as a plausible 32 °F.
 */
export const displayTemperature = (
  celsius: unknown,
  unit: TemperatureUnit,
  decimals = 1,
): number | null => {
  if (celsius == null || celsius === "") return null;
  const n = Number(celsius);
  if (!Number.isFinite(n)) return null;
  return round(toDisplayTemperature(n, unit), decimals);
};

/**
 * The one unit symbol the rows agree on under `unitsName`, else `fallback`.
 *
 * Area and depth ride on the rows (see columns/units): each row names its own
 * unit, so a US view says `in2` and an AUS view `mm`. A view that projects no
 * units column, or an All Locations set that mixes systems, has nothing
 * row-level to say, and the org's own system decides.
 */
export const rowsUnitSymbol = (
  rows: readonly Record<string, unknown>[],
  unitsName: string,
  fallback: string,
): string => {
  const symbols = collectUnitSymbols(rows, unitsName);
  return symbols.length === 1 ? symbols[0] : fallback;
};
